/**
 * @fileoverview Module catalog status policy — the shared decision rules for
 * whether a catalog module may be enabled on a tenant.
 * @module core-domain/modules/moduleStatus
 *
 * Responsibilities:
 * - Identify deprecated catalog entries
 * - Decide whether a module may be newly enabled
 * - Decide whether a catalog entry defaults to enabled
 *
 * Dependencies: none — pure helpers; hosts inject catalog entries.
 *
 * Architecture:
 * - Shared by every host that renders module-selection UI (system-web
 *   department edit, onboarding wizard). Server-side creation validation in
 *   api/system-api tenantModuleResolver enforces this contract at the
 *   provisioning boundary.
 *
 * Notes:
 * - 'deprecated' means "no new use": an entry that is already enabled may be
 *   disabled, but an entry that is not enabled cannot be newly enabled.
 */

export const MODULE_STATUS_DEPRECATED = 'deprecated';

export function isDeprecatedModuleStatus(status: string | null | undefined): boolean {
  // Normalize before comparing: catalog entries imported from JSON or written
  // by legacy tooling may carry case/whitespace variants that would otherwise
  // slip past the deprecated gate and stay enableable.
  return status?.trim().toLowerCase() === MODULE_STATUS_DEPRECATED;
}

/**
 * @contract ModuleCatalogStatusEntry
 * Purpose: Minimal catalog shape needed for status-policy decisions.
 * Fields:  status — catalog lifecycle status; enabledByDefault — seed default
 * Rules:   both fields optional; absent status is treated as non-deprecated
 * Sync:    catalog is system-owned; propagated to hosts via listModules
 */
export interface ModuleCatalogStatusEntry {
  status?: string | null;
  enabledByDefault?: boolean | null;
}

/**
 * Whether the module may be enabled (turned on) right now.
 * A deprecated entry returns false — enabling it is "new use". Callers combine
 * this with their own invariants (e.g. last-enabled-module lock) for the full
 * disabled state of a checkbox.
 */
export function canEnableModuleSelection(
  entry: ModuleCatalogStatusEntry | null | undefined,
): boolean {
  return !isDeprecatedModuleStatus(entry?.status);
}

/**
 * Whether a catalog entry defaults to enabled when no explicit selection
 * exists. Deprecated entries never default on — seeding a default-on
 * deprecated module would generate a selection the creation contract rejects.
 */
export function isModuleEnabledByDefault(entry: ModuleCatalogStatusEntry): boolean {
  return Boolean(entry.enabledByDefault) && !isDeprecatedModuleStatus(entry.status);
}

/**
 * @contract TenantModuleSelectionEntry
 * Purpose: Minimal shape of one entry in a tenant document's `modules[]`
 *   selection array.
 * Fields:  code — catalog module code; isEnabled — tenant's on/off choice
 * Rules:   entries without a non-empty string code or without `isEnabled === true`
 *   never count as enabled
 * Sync:    the selection is system-owned; tenant-web reads it via the synced
 *   `system_tenant_ref` doc, mobile-pwa via the synced `tenant_modules`
 *   collection.
 */
export interface TenantModuleSelectionEntry {
  code?: string;
  isEnabled?: boolean;
  [key: string]: unknown;
}

/**
 * Extracts the enabled module codes from a tenant `modules[]` selection —
 * the shared derivation used by every host that gates UI on enabled modules.
 * Returns [] when the selection is absent or malformed; callers that need to
 * distinguish "no config" from "nothing enabled" must check for a missing
 * selection BEFORE calling this.
 */
export function extractEnabledModuleCodes(modules: unknown): string[] {
  if (!Array.isArray(modules)) return [];
  return (modules as TenantModuleSelectionEntry[])
    .filter((m) => m?.isEnabled === true && typeof m.code === 'string' && m.code.trim() !== '')
    .map((m) => m.code as string);
}

/**
 * Extracts enabled module codes from a Dexie result set of tenant
 * documents — picks the first non-deleted document and reads its `modules[]`
 * selection. Shared by tenant-web (`system_tenant_ref` rows) and mobile-pwa
 * (`tenant_modules` projection rows) so both hosts apply the identical
 * doc-selection and enabled-code contract.
 * Returns [] when no live document carries a selection.
 */
export function extractEnabledModuleCodesFromDocs(docs: unknown): string[] {
  const list = Array.isArray(docs) ? docs : [];
  const tenant = list.find((doc) => !(doc as { _deleted?: boolean })?._deleted) as
    | { modules?: unknown }
    | undefined;
  return extractEnabledModuleCodes(tenant?.modules);
}
